import { forwardRef, useEffect, useRef, useState } from 'react';
import { Avatar } from '@mui/material';
import type { AvatarProps } from '@mui/material';
import { avatarBarIndex, initials } from '../../utils/avatarHash';
import { loadAvatarObjectUrl } from '../../utils/avatarImageCache';
import { radius } from '../../theme/tokens';
import { companyIconFor } from './companyIcons';

export interface CompanyAvatarProps extends Omit<AvatarProps, 'src' | 'children' | 'color'> {
  /** Company name — drives the initials + hashed fallback colour. */
  name: string | null | undefined;
  /**
   * Owned-logo URL (`/api/images/:id`); highest priority in the fallback chain.
   * Prefer `logoId` where only the raw id is to hand (the `company` include
   * carries `logoId` on every surface) — this URL wins if both are supplied.
   */
  logoUrl?: string | null;
  /** Owned-logo image id; resolved to `/api/images/:id` when no `logoUrl` given. */
  logoId?: string | null;
  /** Curated icon-set key; rendered when there's no logo. */
  icon?: string | null;
  /** Brand hex colour; tints the icon/initials tile. null → hashed fallback (AC-E4). */
  color?: string | null;
  /** Square size in px (default 28). */
  size?: number;
  /** Override the auto-derived initials font size. */
  fontSize?: number | string;
  /** Accessible label — exposes the avatar as `role="img"` with this name. */
  ariaLabel?: string;
}

/**
 * Perceived-luminance contrast ink for an arbitrary user-chosen brand colour.
 * A brand tile is intentionally mode-invariant (a company's colour looks the
 * same in light and dark), so the ink is a fixed literal rather than a palette
 * key — you can't theme a contrast colour for a colour we don't control.
 * Light backgrounds get near-black ink, dark backgrounds get white.
 */
export function readableInk(hex: string): string {
  const c = hex.replace('#', '').trim();
  const full =
    c.length === 3
      ? c
          .split('')
          .map((x) => x + x)
          .join('')
      : c;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return '#ffffff';
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? 'rgba(17,24,39,0.92)' : '#ffffff';
}

/**
 * The one shared company avatar (AC-25/26). Fallback chain:
 *   logo image  →  icon + brand colour  →  initials + brand colour  →
 *   initials + hashed colour (the established `bars[(hash(name)%8)+1]` rule).
 *
 * A company with no branding renders the same hashed initials tile it always
 * has (AC-E4). Auth-gated logos are fetched with the bearer token and shown as
 * a blob URL (`avatarImageCache`), lazy-loaded via `IntersectionObserver` so a
 * long list only fetches the logos scrolled into view — same mechanism as
 * `PersonAvatar`.
 */
export const CompanyAvatar = forwardRef<HTMLDivElement, CompanyAvatarProps>(
  function CompanyAvatar(
    { name, logoUrl, logoId, icon, color, size = 28, fontSize, ariaLabel, sx, ...rest },
    forwardedRef,
  ) {
    const rootRef = useRef<HTMLDivElement | null>(null);
    const [objectUrl, setObjectUrl] = useState<string | null>(null);
    const [nearViewport, setNearViewport] = useState(false);
    // A raw logoId resolves to the same auth-gated path the companies endpoint's
    // `logoUrl` uses; an explicit `logoUrl` wins if both are given.
    const resolvedLogoUrl = logoUrl ?? (logoId ? `/api/images/${logoId}` : null);
    const barIdx = avatarBarIndex(name);
    // Icon/initials are always the children; a resolved logo image covers them
    // via the Avatar's `src`. So a logo that fails to load falls back to the
    // icon (then initials) rather than a blank tile.
    const IconComponent = companyIconFor(icon);

    // Reset the resolved image whenever the source changes (or clears), so a
    // logo swap doesn't briefly show the previous company's picture.
    useEffect(() => {
      setObjectUrl(null);
    }, [resolvedLogoUrl]);

    // Defer the fetch until near the viewport. jsdom (no IntersectionObserver)
    // loads eagerly.
    useEffect(() => {
      if (!resolvedLogoUrl) return;
      const el = rootRef.current;
      if (!el || typeof IntersectionObserver === 'undefined') {
        setNearViewport(true);
        return;
      }
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            setNearViewport(true);
            observer.disconnect();
          }
        },
        { rootMargin: '200px' },
      );
      observer.observe(el);
      return () => observer.disconnect();
    }, [resolvedLogoUrl]);

    useEffect(() => {
      if (!resolvedLogoUrl || !nearViewport) return;
      let cancelled = false;
      loadAvatarObjectUrl(resolvedLogoUrl)
        .then((url) => {
          if (!cancelled) setObjectUrl(url);
        })
        .catch(() => {
          // Leave the icon/initials fallback in place on any load failure.
          if (!cancelled) setObjectUrl(null);
        });
      return () => {
        cancelled = true;
      };
    }, [resolvedLogoUrl, nearViewport]);

    const brandBg = color || undefined;
    const brandInk = color ? readableInk(color) : undefined;

    return (
      <Avatar
        // Injected props first (Tooltip/Badge handlers, aria-hidden, …); our
        // explicit props below win on any conflict.
        {...rest}
        ref={(node: HTMLDivElement | null) => {
          rootRef.current = node;
          if (typeof forwardedRef === 'function') forwardedRef(node);
          else if (forwardedRef) forwardedRef.current = node;
        }}
        variant="rounded"
        src={objectUrl ?? undefined}
        role={ariaLabel ? 'img' : undefined}
        aria-label={ariaLabel}
        sx={[
          (theme) => ({
            width: size,
            height: size,
            flexShrink: 0,
            // Square-ish tile (radius.bar = 4px) distinguishes a company from a
            // circular person avatar.
            borderRadius: `${radius.bar}px`,
            fontSize: fontSize ?? Math.max(10, Math.round(size * 0.4)),
            fontWeight: 600,
            // Brand colour when set; otherwise the established hashed fallback.
            backgroundColor: brandBg ?? theme.palette.bars[barIdx].fill,
            color: brandInk ?? theme.palette.bars[barIdx].ink,
            '& > svg': { fontSize: Math.round(size * 0.58) },
          }),
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        {IconComponent ? <IconComponent /> : initials(name)}
      </Avatar>
    );
  },
);

export default CompanyAvatar;
